import { useEffect, useState } from "react";
import { ListChecks, Plus, X, Check, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { TASK_PRIORITIES, TASK_TYPES, TASK_STATUSES } from "@/lib/config";
import { Card, Badge, Btn, EmptyState, AInput, ASelect, ATextarea, useSave } from "../AdminUI";

const statusColor = (s) => ({ Completed: "green", Overdue: "red", Cancelled: "gray", "In Progress": "amber" }[s] || "blue");
const prioColor = (p) => ({ High: "red", Medium: "amber", Low: "gray" }[p] || "gray");

function TaskModal({ task, customers, onClose, onSaved }) {
  const [f, setF] = useState(task || { title: "", customer_id: "", due_date: "", due_time: "", priority: "Medium", type: "General Task", notes: "", assigned_to: "" });
  const [saving, run] = useSave();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => { e.preventDefault(); run(async () => {
    if (task) await api.patch(`/admin/tasks/${task.id}`, f); else await api.post("/admin/tasks", f);
    onSaved(); onClose();
  }, "Task saved"); };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="task-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="relative bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-head font-bold text-xl text-[var(--navy)] uppercase">{task ? "Edit Follow-Up" : "New Follow-Up"}</h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <AInput label="Title" value={f.title} onChange={set("title")} required data-testid="task-title" />
        <div className="grid grid-cols-2 gap-3">
          <ASelect label="Customer" value={f.customer_id} options={[{ value: "", label: "— None —" }, ...customers.map((c) => ({ value: c.id, label: `${c.name} (${c.customer_id})` }))]} onChange={set("customer_id")} data-testid="task-customer" />
          <ASelect label="Type" value={f.type} options={TASK_TYPES} onChange={set("type")} />
          <AInput label="Due Date" type="date" value={f.due_date} onChange={set("due_date")} data-testid="task-due" />
          <AInput label="Due Time" type="time" value={f.due_time} onChange={set("due_time")} />
          <ASelect label="Priority" value={f.priority} options={TASK_PRIORITIES} onChange={set("priority")} />
          <AInput label="Assigned To" value={f.assigned_to} onChange={set("assigned_to")} />
        </div>
        <ATextarea label="Notes" value={f.notes} onChange={set("notes")} />
        <div className="flex gap-2 justify-end">
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" loading={saving} data-testid="task-save">Save</Btn>
        </div>
      </form>
    </div>
  );
}

export default function FollowUps() {
  const [tasks, setTasks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [filter, setFilter] = useState("");
  const load = () => api.get("/admin/tasks").then((r) => setTasks(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  const custName = (id) => customers.find((c) => c.id === id)?.name || "";
  const complete = async (t) => { await api.patch(`/admin/tasks/${t.id}`, { status: "Completed" }); load(); };
  const del = async (t) => { if (!window.confirm("Delete this task?")) return; await api.delete(`/admin/tasks/${t.id}`); load(); };
  const shown = filter ? tasks.filter((t) => t.status === filter) : tasks;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Follow-Ups</h1>
        <div className="flex items-center gap-2">
          <select className="h-10 px-3 rounded-md border border-gray-300 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="task-filter">
            <option value="">All</option>{["Overdue", ...TASK_STATUSES].map((s) => <option key={s}>{s}</option>)}
          </select>
          <Btn onClick={() => setShowNew(true)} data-testid="new-task-btn"><Plus className="w-4 h-4" /> New</Btn>
        </div>
      </div>
      <Card>
        {shown.length === 0 ? <EmptyState icon={ListChecks} title="No follow-ups" hint="Create a follow-up task to stay on top of customers." /> : (
          <div className="space-y-2">
            {shown.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50" data-testid={`task-row-${t.id}`}>
                <button onClick={() => complete(t)} disabled={t.status === "Completed"} className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${t.status === "Completed" ? "bg-green-500 border-green-500 text-white" : "border-gray-300 hover:border-green-500"}`} data-testid={`complete-${t.id}`}>
                  {t.status === "Completed" && <Check className="w-4 h-4" />}
                </button>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setEdit(t)}>
                  <p className={`font-semibold text-sm ${t.status === "Completed" ? "line-through text-gray-400" : "text-[var(--navy)]"}`}>{t.title}</p>
                  <p className="text-xs text-gray-400">{t.type}{custName(t.customer_id) && ` · ${custName(t.customer_id)}`}{t.due_date && ` · Due ${t.due_date}`}</p>
                </div>
                <Badge color={prioColor(t.priority)}>{t.priority}</Badge>
                <Badge color={statusColor(t.status)}>{t.status}</Badge>
                <button onClick={() => del(t)} data-testid={`delete-task-${t.id}`}><Trash2 className="w-4 h-4 text-gray-300 hover:text-red-500" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
      {showNew && <TaskModal customers={customers} onClose={() => setShowNew(false)} onSaved={load} />}
      {edit && <TaskModal task={edit} customers={customers} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}
